import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { LanguageSelector } from '~/components/LanguageSelector'
import { ScrollView } from '~/components/ScrollView'
import { Badge } from '~/components/ui/badge'
import { LinkButton } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'
// @ts-ignore
import Logo from '~/media/aikoa-logo-sand.svg'
import { useScrollContext } from '../layout/ScrollContext'
import { PolicyLinks } from '../user/PolicyLinks'
import { FrontPageFaq } from './FrontPageFaq'
import { PublicFrontPageBuyers } from './PublicFrontPageBuyers'
import { PublicFrontPageRealtors } from './PublicFrontPageRealtors'
import { TransUnderlinedText } from './UnderlinedText'

export type FrontPageRole = 'realtor' | 'buyer'

const LOGO_SHRINK_SCROLL_DISTANCE = 100
const LOGO_MAX_HEIGHT = 66
const LOGO_MIN_HEIGHT = 28
const LOGO_MAX_WIDTH = 250
const LOGO_MIN_WIDTH = 104
const LOGO_RESTORE_ZONE = LOGO_SHRINK_SCROLL_DISTANCE * 0.05

const SMOOTH_SPRING_CONFIG = {
  damping: 50,
  stiffness: 400,
  mass: 1,
}

function RoleToggle({
  role,
  onRoleChange,
  className,
}: {
  role: FrontPageRole
  onRoleChange: (role: FrontPageRole) => void
  className?: string
}) {
  const { t } = useTranslations('home')

  return (
    <View className={cn('flex-row flex-wrap items-center justify-center gap-2', className)}>
      <Pressable
        onPress={() => onRoleChange('realtor')}
        className={cn(
          'rounded-full px-4 py-2.5 sm:px-5',
          role === 'realtor' ? 'bg-white' : 'border border-white/20 bg-white/10'
        )}>
        <Text
          className={cn(
            'text-center font-normal text-sm',
            role === 'realtor' ? 'font-semibold text-primary' : 'text-white'
          )}>
          {t(($) => $.frontpage.roleRealtor)}
        </Text>
      </Pressable>

      <Pressable
        onPress={() => onRoleChange('buyer')}
        className={cn(
          'rounded-full px-4 py-2.5 sm:px-5',
          role === 'buyer' ? 'bg-white' : 'border border-white/20 bg-white/10'
        )}>
        <Text
          className={cn(
            'text-center font-normal text-sm',
            role === 'buyer' ? 'font-semibold text-primary' : 'text-white'
          )}>
          {t(($) => $.frontpage.roleBuyer)}
        </Text>
      </Pressable>
    </View>
  )
}

const CTA_EMAIL = 'contact@aikoa.fi'

function HeroSection({ role }: { role: FrontPageRole }) {
  const { t } = useTranslations('home')

  const heroSubtitle =
    role === 'realtor'
      ? t(($) => $.frontpage.realtor.heroSubtitle)
      : t(($) => $.frontpage.buyer.heroSubtitle)

  const blurbSubtitle =
    role === 'realtor'
      ? t(($) => $.frontpage.realtor.statementSubtitle)
      : t(($) => $.frontpage.buyer.statementSubtitle)

  const ctaLabel =
    role === 'realtor'
      ? t(($) => $.frontpage.realtor.ctaLabel)
      : t(($) => $.frontpage.buyer.ctaLabel)

  const titleClassName =
    'drop-shadow-secondary/50 mx-auto text-nowrap text-center font-medium text-5xl leading-snug tracking-tight text-primary drop-shadow-2xl sm:text-6xl md:text-7xl md:leading-none lg:text-8xl'

  return (
    <View className="relative z-10 mx-auto w-full max-w-7xl flex-1 justify-evenly gap-8 px-6 md:gap-12 md:px-6 lg:gap-14 xl:px-0">
      <View className="justify-start pt-4 md:pt-0">
        <TransUnderlinedText
          className={cn(titleClassName, role === 'buyer' && 'text-pretty font-bold')}
          i18nKey={
            role === 'realtor'
              ? ($) => $.frontpage.realtor.heroTitle1
              : ($) => $.frontpage.buyer.heroTitle
          }
        />
        {role === 'realtor' && (
          <TransUnderlinedText
            className={cn(titleClassName, 'font-bold')}
            i18nKey={($) => $.frontpage.realtor.heroTitle2}
          />
        )}
      </View>
      <View className="relative gap-2">
        <View className="mask-radial-from-20% mask-radial-to-70% pointer-events-none absolute -inset-x-10 -inset-y-20 z-0 md:-inset-y-16 md:inset-x-16">
          <View className="h-full w-full bg-white/20 md:bg-white/15" />
        </View>
        <Text className="drop-shadow-tertiary-foreground/30 text-center font-light text-lg leading-relaxed text-primary drop-shadow-lg sm:text-xl">
          {heroSubtitle}
        </Text>
        <Text className="drop-shadow-tertiary-foreground/0 text-center font-medium text-lg leading-relaxed text-primary drop-shadow-lg sm:text-xl">
          {blurbSubtitle}
        </Text>
      </View>

      {role === 'realtor' && (
        <View className="w-full max-w-md flex-col items-stretch gap-3 self-center sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          <LinkButton
            variant="primary"
            size="lg"
            href={`mailto:${CTA_EMAIL}`}
            className="w-full sm:w-auto">
            {ctaLabel}
          </LinkButton>

          <LinkButton variant="outline" size="lg" href="/login" className="w-full sm:w-auto">
            {t(($) => $.frontpage.alreadyHaveAccount)}
          </LinkButton>
        </View>
      )}
    </View>
  )
}

function FooterSection({ role }: { role: FrontPageRole }) {
  const { t } = useTranslations('home')

  const ctaTitle =
    role === 'realtor'
      ? t(($) => $.frontpage.footer.realtor.ctaTitle)
      : t(($) => $.frontpage.footer.buyer.ctaTitle)

  return (
    <View className="w-full rounded-t-content bg-primary px-4 py-16 xs:px-5 sm:px-10 sm:py-24">
      <View className="mx-auto w-full max-w-4xl items-center gap-6 sm:gap-8">
        <Text className="text-center font-semibold text-3xl leading-tight tracking-tight text-white">
          {ctaTitle}
        </Text>

        <LinkButton
          variant="primary"
          size="lg"
          href={`mailto:${CTA_EMAIL}`}
          className="w-full sm:w-auto">
          {CTA_EMAIL}
        </LinkButton>

        <PolicyLinks theme="light" className="mt-2" />
      </View>
    </View>
  )
}

export function PublicFrontPage() {
  const { t } = useTranslations('home')
  const { scrollY } = useScrollContext()
  const [role, setRole] = useState<FrontPageRole>('realtor')

  const hasScrolledPastThreshold = useSharedValue(false)

  const logoHeight = useDerivedValue(() => {
    if (scrollY.value > LOGO_SHRINK_SCROLL_DISTANCE) {
      hasScrolledPastThreshold.value = true
    } else if (scrollY.value <= LOGO_RESTORE_ZONE) {
      hasScrolledPastThreshold.value = false
    }

    if (hasScrolledPastThreshold.value) {
      return withTiming(LOGO_MIN_HEIGHT, { duration: 200 })
    }

    return withSpring(
      interpolate(
        scrollY.value,
        [0, LOGO_SHRINK_SCROLL_DISTANCE],
        [LOGO_MAX_HEIGHT, LOGO_MIN_HEIGHT],
        Extrapolation.CLAMP
      ),
      SMOOTH_SPRING_CONFIG
    )
  })

  const headerAnimatedStyle = useAnimatedStyle(() => ({
    paddingTop: withSpring(
      interpolate(
        scrollY.value,
        [0, LOGO_SHRINK_SCROLL_DISTANCE + 40],
        [48, 21],
        Extrapolation.CLAMP
      ),
      SMOOTH_SPRING_CONFIG
    ),
    paddingBottom: withSpring(
      interpolate(
        scrollY.value,
        [0, LOGO_SHRINK_SCROLL_DISTANCE + 40],
        [0, 21],
        Extrapolation.CLAMP
      ),
      SMOOTH_SPRING_CONFIG
    ),
  }))

  const headerBgAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        scale: withSpring(
          interpolate(
            scrollY.value,
            [0, LOGO_SHRINK_SCROLL_DISTANCE + 40],
            [1.2, 1],
            Extrapolation.CLAMP
          ),
          SMOOTH_SPRING_CONFIG
        ),
      },
    ],

    backgroundColor: interpolateColor(
      scrollY.value,
      [0, LOGO_SHRINK_SCROLL_DISTANCE],
      ['transparent', designTokens.secondary]
    ),
  }))

  const logoAnimatedStyle = useAnimatedStyle(() => ({
    height: logoHeight.value,
    width: interpolate(
      scrollY.value,
      [0, LOGO_SHRINK_SCROLL_DISTANCE],
      [LOGO_MAX_WIDTH, LOGO_MIN_WIDTH],
      Extrapolation.CLAMP
    ),
  }))

  const badgeAnimatedStyle = useAnimatedStyle(() => ({
    right: interpolate(
      scrollY.value,
      [0, LOGO_SHRINK_SCROLL_DISTANCE],
      [-17.5, -30],
      Extrapolation.CLAMP
    ),
  }))

  const sloganAnimatedStyle = useAnimatedStyle(() => ({
    color: interpolateColor(
      scrollY.value,
      [0, LOGO_SHRINK_SCROLL_DISTANCE],
      [designTokens.tertiaryForeground, designTokens.tertiaryForeground]
    ),
    fontSize: interpolate(
      scrollY.value,
      [0, LOGO_SHRINK_SCROLL_DISTANCE],
      [14, 10],
      Extrapolation.CLAMP
    ),
  }))

  const handleRoleChange = useCallback((newRole: FrontPageRole) => {
    setRole(newRole)
  }, [])

  const blurbTitle =
    role === 'realtor'
      ? t(($) => $.frontpage.realtor.statementTitle)
      : t(($) => $.frontpage.buyer.statementTitle)

  return (
    <>
      <View className="relative z-50 overflow-hidden">
        <View className="fixed -right-1 -top-1 z-50 flex-row items-center gap-0.5 overflow-hidden rounded-bl-content-inner bg-primary px-1 py-1 md:right-4 md:top-4 md:rounded-full md:border md:border-white/25 md:bg-white/50 md:shadow-sm md:shadow-primary/50">
          <LanguageSelector iconClassName="md:text-foreground text-white" />
          <LinkButton variant="ghost" size="lg" href="/login" className="px-2.5 py-2">
            <Icon name="lock-closed" size={18} className="text-white md:text-foreground" />
          </LinkButton>
        </View>
      </View>
      <Animated.View
        className="absolute left-0 right-0 top-6 z-20 mx-auto box-content w-[calc(100%-5.5rem)] max-w-[476px] items-center justify-center gap-2 rounded-full px-2 py-6 md:top-3 md:w-auto"
        style={headerAnimatedStyle}>
        <Animated.View
          className="absolute inset-0 h-full w-full rounded-full"
          style={headerBgAnimatedStyle}
        />
        <View className="relative h-full flex-row items-center justify-center">
          <Animated.View style={logoAnimatedStyle}>
            <Logo
              height="100%"
              width="100%"
              fill={designTokens.tertiaryForeground}
              className="h-full w-full"
            />
          </Animated.View>
          <Badge
            variant="secondary"
            className="absolute -right-5 top-0 bg-primary"
            style={badgeAnimatedStyle}
            textClassName="text-background! text-xxxs"
            size="xxs">
            {t(($) => $.logoBadge)}
          </Badge>
        </View>
        <Animated.Text
          style={sloganAnimatedStyle}
          className="-mb-1 self-center text-pretty text-center font-medium text-base uppercase leading-tight tracking-wider">
          {blurbTitle}
        </Animated.Text>
      </Animated.View>

      <ScrollView
        className="relative z-10 w-full flex-1 rounded-content md:rounded-none"
        scrollToOverflowEnabled
        offsetTrackHeight
        indicatorClassName="right-0 sm:right-0"
        contentContainerClassName="w-full items-center justify-start gap-8 md:gap-24">
        <View
          className={cn(
            'pt-58 relative min-h-[90vh] w-full gap-4 px-0 pb-24 sm:h-auto md:min-h-0 md:gap-8 md:py-64 xl:mx-auto'
          )}>
          <View className="mask-b-from-85% lg:mask-radial-from-50% lg:mask-radial-to-90% absolute inset-0 -bottom-24 z-0 rounded-t-content backdrop-blur-2xl sm:rounded-none md:-bottom-10">
            <View className="h-full w-full bg-white/15" />
          </View>
          <RoleToggle
            role={role}
            onRoleChange={handleRoleChange}
            className="self-center px-3 sm:px-0"
          />
          <HeroSection role={role} />
        </View>
        <View className={cn('w-full md:-mt-60')}>
          {role === 'realtor' ? <PublicFrontPageRealtors /> : <PublicFrontPageBuyers />}
        </View>
        <View className="w-full gap-16 rounded-t-content bg-tertiary-foreground sm:gap-24">
          <FrontPageFaq role={role} />
          <FooterSection role={role} />
        </View>
      </ScrollView>
    </>
  )
}
